// ========================================================
// FAST HIT Website — Matches Service
// Read-only queries for public match discovery with filters & search
// Real-time Firestore listeners — no dummy data fallbacks
// ========================================================

import {
  collection,
  query,
  limit,
  getDocs,
  onSnapshot,
  type DocumentData,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { COLLECTIONS } from '../types/collections';
import type { Match, MatchFormat, MatchStatus } from '../types';

function asScore(raw: unknown, fallback = '0/0'): string {
  if (raw == null) return fallback;
  if (typeof raw === 'string' && raw.trim()) return raw.trim();
  if (typeof raw === 'number' && Number.isFinite(raw)) return String(raw);
  if (typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    const runs = obj.runs ?? obj.totalRuns ?? obj.score;
    const wickets = obj.wickets ?? obj.totalWickets;
    if (runs != null) {
      return wickets != null ? `${runs}/${wickets}` : String(runs);
    }
  }
  return fallback;
}

function asOvers(raw: unknown, fallback = '0.0'): string {
  if (raw == null) return fallback;
  if (typeof raw === 'string' && raw.trim()) return raw.trim();
  if (typeof raw === 'number' && Number.isFinite(raw)) return String(raw);
  return fallback;
}

function teamDisplayName(data: DocumentData | undefined, fallback: string): string {
  const name = String(data?.name || '').trim();
  return name || fallback;
}

function teamShortName(data: DocumentData | undefined, name: string, fallback: string): string {
  // Prefer full name for short labels when no dedicated shortName exists —
  // avoids truncated / typo-prone short codes drifting from the real team name.
  const short = String(data?.shortName || '').trim();
  if (short && short.toLowerCase() !== name.toLowerCase()) {
    // Only keep shortName when it looks intentional (not a garbled slice).
    if (short.length >= 2 && short.length <= 12) return short;
  }
  if (name.length <= 12) return name;
  return name.slice(0, 4).toUpperCase() || fallback;
}

function mapDocToMatch(id: string, data: DocumentData): Match {
  const rawStatus = String(data.status || data.state || 'scheduled')
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  const normalizedStatus: MatchStatus =
    rawStatus === 'inningsbreak' || rawStatus === 'innings_break'
      ? 'innings_break'
      : rawStatus === 'live' || rawStatus === 'toss'
      ? 'live'
      : rawStatus === 'completed'
      ? 'completed'
      : rawStatus === 'cancelled' || rawStatus === 'abandoned'
      ? 'cancelled'
      : 'scheduled';

  const scheduledAt =
    data.scheduledAt?.toDate?.() ||
    data.date?.toDate?.() ||
    (data.scheduledAt
      ? new Date(data.scheduledAt)
      : data.date
      ? new Date(data.date)
      : new Date());

  const groundName =
    data.groundName ||
    data.venue?.name ||
    'Local Ground';

  const city = data.city || data.venue?.city || undefined;

  let rawFormat = String(data.format || data.matchType || 'T20').toUpperCase();
  if (rawFormat === 'T50') rawFormat = 'ODI';
  if (rawFormat === 'CUSTOM' || rawFormat === 'T30' || rawFormat === 'T40') rawFormat = 'Custom';

  const teamAName = teamDisplayName(data.teamA, 'Team Alpha');
  const teamBName = teamDisplayName(data.teamB, 'Team Bravo');

  return {
    id,
    tournamentId: data.tournamentId,
    tournamentName: data.tournamentName,
    format: rawFormat as MatchFormat,
    status: normalizedStatus,
    scheduledAt: Number.isNaN(scheduledAt.getTime()) ? new Date() : scheduledAt,
    startedAt: data.startedAt?.toDate?.() || (data.startedAt ? new Date(data.startedAt) : undefined),
    completedAt: data.completedAt?.toDate?.() || (data.completedAt ? new Date(data.completedAt) : undefined),
    groundId: data.groundId || data.venue?.ground_id || data.venue?.groundId,
    groundName,
    city,
    teamA: {
      id: data.teamA?.id || 'team-a',
      name: teamAName,
      shortName: teamShortName(data.teamA, teamAName, 'TMA'),
      logoUrl: data.teamA?.logoUrl || data.teamA?.logo,
      score: asScore(data.teamA?.score),
      overs: asOvers(data.teamA?.overs),
    },
    teamB: {
      id: data.teamB?.id || 'team-b',
      name: teamBName,
      shortName: teamShortName(data.teamB, teamBName, 'TMB'),
      logoUrl: data.teamB?.logoUrl || data.teamB?.logo,
      score: asScore(data.teamB?.score),
      overs: asOvers(data.teamB?.overs),
    },
    toss: data.toss,
    officials: data.officials || [],
    resultString: data.resultString,
    playerOfTheMatchId: data.playerOfTheMatchId,
    playerOfTheMatchName: data.playerOfTheMatchName,
    isScoringLocked: Boolean(data.isScoringLocked),
    createdBy: data.createdBy || '',
    scorerId: data.scorerId,
  };
}

function sortMatches(matches: Match[]): Match[] {
  const rank = (s: MatchStatus) =>
    s === 'live' || s === 'innings_break' ? 0 : s === 'scheduled' ? 1 : s === 'completed' ? 2 : 3;
  return [...matches].sort((a, b) => {
    const r = rank(a.status) - rank(b.status);
    if (r !== 0) return r;
    return b.scheduledAt.getTime() - a.scheduledAt.getTime();
  });
}

/** Fetch current live matches (limit 4) */
export async function getLiveMatches(maxItems = 4): Promise<Match[]> {
  try {
    const q = query(
      collection(db, COLLECTIONS.MATCHES),
      limit(50)
    );
    const snap = await getDocs(q);
    const matches = snap.docs.map((d) => mapDocToMatch(d.id, d.data()));
    return matches.filter((m) => m.status === 'live' || m.status === 'innings_break').slice(0, maxItems);
  } catch (err) {
    console.warn('Could not fetch live matches from Firestore:', err);
    return [];
  }
}

/**
 * Real-time listener for live matches — streams updates via onSnapshot
 */
export function subscribeLiveMatches(
  callback: (matches: Match[]) => void,
  maxItems = 6
): Unsubscribe {
  const q = query(
    collection(db, COLLECTIONS.MATCHES),
    limit(50)
  );

  return onSnapshot(
    q,
    (snap) => {
      const matches = snap.docs
        .map((d) => mapDocToMatch(d.id, d.data()))
        .filter((m) => m.status === 'live' || m.status === 'innings_break')
        .slice(0, maxItems);
      callback(matches);
    },
    (err) => {
      console.warn('Live matches subscription error:', err);
      callback([]);
    }
  );
}

/** Fetch recent completed matches (limit 4) */
export async function getRecentMatches(maxItems = 4): Promise<Match[]> {
  try {
    const q = query(
      collection(db, COLLECTIONS.MATCHES),
      limit(50)
    );
    const snap = await getDocs(q);
    const matches = snap.docs.map((d) => mapDocToMatch(d.id, d.data()));
    return matches.filter((m) => m.status === 'completed').slice(0, maxItems);
  } catch (err) {
    console.warn('Could not fetch recent matches from Firestore:', err);
    return [];
  }
}

export type MatchesFilterOptions = {
  tab?: 'all' | 'live' | 'upcoming' | 'completed';
  format?: 'all' | MatchFormat;
  search?: string;
  limitCount?: number;
};

/**
 * Filtered query supporting status tabs, formats, and search.
 * Same status mapping as subscribeLiveMatches (state + status).
 */
export async function getFilteredMatches(options: MatchesFilterOptions = {}): Promise<{
  matches: Match[];
  isDemo: boolean;
}> {
  const { tab = 'all', format = 'all', search = '', limitCount = 40 } = options;

  try {
    const q = query(collection(db, COLLECTIONS.MATCHES), limit(Math.max(limitCount * 2, 80)));
    const snap = await getDocs(q);

    let results = snap.docs.map((d) => mapDocToMatch(d.id, d.data()));

    if (tab === 'live') {
      results = results.filter((m) => m.status === 'live' || m.status === 'innings_break');
    } else if (tab === 'upcoming') {
      results = results.filter((m) => m.status === 'scheduled');
    } else if (tab === 'completed') {
      results = results.filter((m) => m.status === 'completed');
    }

    if (format !== 'all') {
      results = results.filter((m) => m.format.toLowerCase() === format.toLowerCase());
    }

    if (search.trim()) {
      const queryTerm = search.toLowerCase();
      results = results.filter(
        (m) =>
          m.teamA.name.toLowerCase().includes(queryTerm) ||
          m.teamB.name.toLowerCase().includes(queryTerm) ||
          m.teamA.shortName.toLowerCase().includes(queryTerm) ||
          m.teamB.shortName.toLowerCase().includes(queryTerm) ||
          (m.tournamentName && m.tournamentName.toLowerCase().includes(queryTerm)) ||
          m.groundName.toLowerCase().includes(queryTerm)
      );
    }
    return { matches: sortMatches(results).slice(0, limitCount), isDemo: false };
  } catch (err) {
    console.warn('Could not query matches from Firestore:', err);
    return { matches: [], isDemo: false };
  }
}
